import type { User } from '@creator/sdk/modules/account/account.model';
import Button from '@creator/ui/components/Button/ButtonV2';
import Helptip from '@creator/ui/components/Helptip/Helptip';
import RadioButton from '@creator/ui/components/RadioButton/RadioButton';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import AlertBox from '@src/basic-components/AlertBox/AlertBox';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import useDidUpdateEffect from '@src/hooks/use-did-update-effect';
import { useStoreState } from '@src/model/hooks';
import { getMergeableUsers } from '@src/model/user/helpers';
import { noop } from '@src/utils/utils';
import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export interface ChooseLoginUserProps {
    className?: string;
    users: User[];
    initialSelectedUser?: User;
    submitButtonText?: string;
    alertBoxText?: string;
    onUserClick?: (user: User) => void;
    onSubmit?: (user: User) => void;
    onMergeAccountClick: (user: User) => void;
}

const ChooseLoginUser: FC<ChooseLoginUserProps> = props => {
    const { submitButtonText, alertBoxText, initialSelectedUser, onUserClick, onSubmit = noop, onMergeAccountClick, users, className } = props;

    const navigate = useNavigate();
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);

    const [selectedUser, setSelectedUser] = useState(initialSelectedUser || users[0]);

    const { t } = useTranslation();

    // if user has been logged in, and then decided to logout
    useDidUpdateEffect(() => {
        if (!isLoggedIn) navigate('/');
    }, [isLoggedIn, navigate]);

    function _onUserClick(user: User): void {
        if (!onUserClick) return;

        setSelectedUser(user);
        onUserClick(user);
    }

    function isMergeAccountEnabled(): boolean {
        const mergeableUsers = getMergeableUsers(users);
        if (!mergeableUsers.length) return false;

        const mainAccount = users.find(user => user.mainAccount);
        if (mainAccount && !selectedUser.mainAccount) return false; // if user already have main account, and its not the selected one

        const nonTokenOwnersBesidesSelectedUser = users.filter(user => (!user.tokenOwner || !user.tokenOwner.length) && user.id !== selectedUser.id);
        if (!nonTokenOwnersBesidesSelectedUser.length) return false; // all users in the list are token owners.. we can't merge them

        return true;
    }

    function renderUserTokenOwnershipError(user: User) {
        const isActive = selectedUser.id === user.id;

        if (isActive || !user.tokenOwner || !user.tokenOwner.length) return null;

        const ownedTokens = user.tokenOwner.slice(0, 3).join(', ');
        return (
            <div className="flex items-center mt-3 space-x-1 text-red-500 pl-7 ">
                <Text className="text-sm font-medium">{t('chooseLoginUser.userUnableToMergeDueToTokenOwernship')}</Text>
                <Helptip text={t('chooseLoginUser.userUnableToMergeDueToTokenOwernshipHelpTip', { ownedTokens })} />
            </div>
        );
    }

    function renderUserIsMainAccountError(user: User) {
        const isActive = selectedUser.id === user.id;

        if (isActive || !user.mainAccount) return null;

        return (
            <div className="flex items-center mt-3 space-x-1 text-red-500 pl-7 ">
                <Text className="text-sm font-medium">{t('chooseLoginUser.userUnableToMergeMainAccount')}</Text>
            </div>
        );
    }

    function renderOption(user: User) {
        return (
            <div key={user.id} className={onUserClick ? 'cursor-pointer' : ''}>
                <div className="flex items-center" onClick={() => _onUserClick(user)}>
                    {onUserClick && <RadioButton className="mr-3" isActive={selectedUser.id === user.id} />}
                    <ProfileImage className="mr-3 w-7 h-7" displayName={user.displayName} />
                    <UserDisplayName textProps={{ className: 'font-bold' }} displayName={user.displayName} />
                    {user.mainAccount && <Text className="ml-1 text-gray-500">({t('chooseLoginUser.mainAccountLabel')})</Text>}
                </div>
                {renderUserTokenOwnershipError(user)}
                {renderUserIsMainAccountError(user)}
            </div>
        );
    }
    function renderOptions() {
        return users.map(renderOption);
    }

    function renderAlertBox() {
        return (
            <AlertBox className="mt-5" type="success">
                {alertBoxText || t('chooseLoginUser.aletBox')}
            </AlertBox>
        );
    }

    return (
        <div className={`${className}`}>
            <div className="mt-5 space-y-5">{renderOptions()}</div>
            {renderAlertBox()}
            <div className="flex justify-center space-x-5 mt-7">
                <Button size="sm" color="base" className="w-40" onClick={() => onSubmit(selectedUser)}>
                    <Text>{submitButtonText || t('chooseLoginUser.submitButton')}</Text>
                </Button>
                <Button isDisabled={!isMergeAccountEnabled()} size="sm" className="w-40" onClick={() => onMergeAccountClick(selectedUser)}>
                    <Text>{t('chooseLoginUser.mergeAccounts')}</Text>
                </Button>
            </div>
        </div>
    );
};

export default ChooseLoginUser;
